import i18next, { type TFunction } from "i18next";

import type { AnalysisSnapshot } from "@/api/types";
import { formatDecimal } from "@/lib/utils";

/**
 * Build the per-part meta line shown under a file name —
 * "90×60×40 mm · 38.5 cm³ · ~2.4 h tisk" for 3D prints,
 * "90×60 mm · 12 otv. · řez 1.20 m" for DXF.
 * Returns null if the analysis snapshot is empty.
 *
 * Lives here rather than in ``PartCard`` because the cart is no longer
 * the only place that has to say WHICH part a number is about: the
 * start-up wizard asks "za kolik byste vyrobili tenhle díl" about the
 * operation's own sample part (KAN-1129), and it must show the same
 * geometry in the same words the cart uses.
 *
 * Which metrics appear is driven by the fields the analyzer actually
 * produced — never by the process name — so a new process surfaces its
 * numbers without a branch here. ``t`` must come from the ``cart``
 * namespace, which owns the ``partCard.*`` wording.
 */
export function buildPartMeta(
  analysis: AnalysisSnapshot | null | undefined,
  t: TFunction,
): string | null {
  if (!analysis) return null;
  // Desetinná čísla podle jazyka stránky (KAN-1644), ne ``toFixed`` s tečkou.
  const locale = i18next.language;
  const parts: string[] = [];
  const bbox = analysis.bounding_box;
  if (Array.isArray(bbox) && bbox.length === 4) {
    const [xmin, ymin, xmax, ymax] = bbox;
    const w = Math.round(xmax - xmin);
    const h = Math.round(ymax - ymin);
    if (w > 0 && h > 0) parts.push(`${w}×${h} mm`);
  } else if (bbox && typeof bbox === "object" && "size" in bbox) {
    const [w, h, d] = bbox.size;
    parts.push(`${Math.round(w)}×${Math.round(h)}×${Math.round(d)} mm`);
  }
  if (typeof analysis.volume_mm3 === "number" && analysis.volume_mm3 > 0) {
    parts.push(`${formatDecimal(analysis.volume_mm3 / 1000, locale, 1)} cm³`);
  }
  const printSec = analysis.estimate?.print_time_seconds;
  if (typeof printSec === "number" && printSec > 0) {
    parts.push(
      t("partCard.metaPrintTime", {
        defaultValue: "~{{hours}} h tisk",
        hours: formatDecimal(printSec / 3600, locale, 1),
      }),
    );
  }
  if (typeof analysis.hole_count === "number" && analysis.hole_count > 0) {
    parts.push(
      // KAN-2018 — jeden pojem „otvory" (vnitřní uzavřené kontury, bez
      // vnějšího obrysu); zkratka „otv." vedle „Počet uzavřených kontur"
      // vypadala jako jiná věc.
      t("partCard.metaHoles", {
        defaultValue: "{{count}} otvorů",
        count: analysis.hole_count,
      }),
    );
  }
  if (typeof analysis.total_length === "number" && analysis.total_length > 0) {
    parts.push(
      t("partCard.metaCut", {
        defaultValue: "řez {{meters}} m",
        meters: formatDecimal(analysis.total_length / 1000, locale, 2),
      }),
    );
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}
